import classNames from 'classnames';
import React, {useContext, useEffect} from 'react';
import type {WithRouterProps} from 'react-router';

import appStore from 'client/AppStore';
import {callAction, useFluxStore} from 'client/framework';
import DennisLogo from 'assets/images/dennis-nav-logo.svg';
import DropdownNavItems from './DropdownNavItems/DropdownNavItems';
import {getBaseUserQuery} from 'client/User/UserQueryUtil';
import {GlobalNavBehaviorContext, GlobalNavBehaviorEnum} from './GlobalNavBehavior.context';
import GlobalNavDrawer from './GlobalNavDrawer/GlobalNavDrawer.react';
import globalNavigationActions from './GlobalNavigationActions';
import globalNavigationStore from './GlobalNavigationStore';
import {Logo, NavItem, Icon} from '@albert-io/atomic';
import MainNavItems from './MainNavItems/MainNavItems';
import MarketingNavigation from './MarketingNavigation/MarketingNavigation.react';
import NavWidgets from './NavWidgets/NavWidgets.react';
import {Routes as StudentHomepageRoutes} from '../StudentHomepage/routes';
import sessionStore from 'client/Session/SessionStore';
import useBreakpoint from 'lib/hooks/useBreakpoint';
import {useHasTeacherDashboard} from 'client/components/FeatureFlag/useHasTeacherDashboard';
import UserAvatar from 'generic/UserAvatar/UserAvatar.react';
import {useStudentHomepageStore} from '../StudentHomepage/useStudentHomepageStore';

interface Props extends Pick<WithRouterProps, 'location'> {
  requiresEmailVerification: boolean;
}

export default function GlobalNavigation({location, requiresEmailVerification}: Props) {
  const {pathname} = location;
  const {navBehavior} = useContext(GlobalNavBehaviorContext);
  const {isSAndDown, isMAndUp} = useBreakpoint();
  const userDefaultRoute = useStudentHomepageStore(
    (state) => state.userDefaultRoute ?? StudentHomepageRoutes.Base
  );

  const isStudent = useFluxStore(sessionStore, (s) => s.isStudent());
  const hasSession = useFluxStore(sessionStore, (s) => s.hasValidSession());
  const isDrawerActive = useFluxStore(globalNavigationStore, (s) => s.getIsDrawerActive());
  const isZoneDennis = useFluxStore(appStore, (s) => s.isZoneDennis());
  // Guarded: the selector must not trigger the user fetch pre-session (the early
  // return below this hook block used to prevent that).
  const username = useFluxStore(sessionStore, (s) =>
    s.hasValidSession() ? getBaseUserQuery(s.getUserId()).getResource().getUsername() : ''
  );
  const fullName = useFluxStore(sessionStore, (s) =>
    s.hasValidSession() ? getBaseUserQuery(s.getUserId()).getResource().getFullName() : ''
  );

  const {hasTeacherDashboard} = useHasTeacherDashboard({
    skip: !hasSession
  });
  const goesHome = isStudent || hasTeacherDashboard;

  const shouldShowMarketingNavigation = !requiresEmailVerification && pathname === '/';

  useEffect(() => {
    // `requestId` is optional at runtime but not in the (out-of-scope)
    // `callAction` signature; pass `undefined` to satisfy the arity.
    callAction(globalNavigationActions.SET_PATHNAME, pathname);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!globalNavigationStore.getIsDrawerActive() && isSAndDown) {
      callAction(globalNavigationActions.SET_IS_DRAWER_ACTIVE, true);
    } else if (globalNavigationStore.getIsDrawerActive() && !isSAndDown) {
      callAction(globalNavigationActions.SET_IS_DRAWER_ACTIVE, false);
    }
  }, [isSAndDown]);

  if (shouldShowMarketingNavigation || !hasSession) {
    return <MarketingNavigation location={location} />;
  }

  return (
    <header
      className={classNames('global-nav', {
        'global-nav-not-sticky': navBehavior === GlobalNavBehaviorEnum.VISIBLE_NOT_STICKY
      })}
    >
      <div className='global-nav__content'>
        {isDrawerActive && <GlobalNavDrawer pathname={location.pathname} />}
        <NavItem className='global-nav__title pr-x2' to={goesHome ? userDefaultRoute : '/classes'}>
          {isZoneDennis ? (
            <img
              className='global-nav__dennis-logo'
              src={DennisLogo}
              alt=''
              aria-label='Go to Classes'
            />
          ) : (
            <Logo
              aria-label={`Go ${goesHome ? 'Home' : 'to Classes'}`}
              width='160px'
              color='white'
            />
          )}
        </NavItem>
        {isMAndUp && <MainNavItems />}
        <nav className='global-nav__account' aria-label='account'>
          <ul className='global-nav__account-list'>
            {isMAndUp && <NavWidgets />}
            <DropdownNavItems
              trigger={
                // @ts-expect-error
                <NavItem
                  as='div'
                  aria-label='user menu'
                  active={false}
                  disabled={false}
                  tabIndex={0}
                  role='button'
                  data-testid='global-nav__user-menu-dropdown'
                >
                  <UserAvatar username={username} fullName={fullName} />
                  {isMAndUp && <div className='global-nav__username'>{username}</div>}
                  <Icon icon='caret-down' className='ml-x1' />
                </NavItem>
              }
            />
          </ul>
        </nav>
      </div>
    </header>
  );
}
